import React, { memo, useCallback, useMemo } from "react"
import { Permission } from "../../api"
import { permissionMatrix } from "../../util/permissions"
import { useRole } from "../RoleContext"

const perms = permissionMatrix(Permission)
const subjects = Object.keys(perms).sort((a, b) => a.localeCompare(b))
const groups = [
    { title: "", items: subjects.filter((subject) => !subject.startsWith("Conf_")) },
    { title: "Configurations", items: subjects.filter((subject) => subject.startsWith("Conf_")) },
]

const levelOrder = ["AnyResource", "DirectLink", "IndirectLink"]
const actionOrder = ["Get", "Set", "Delete"]

const styleAdded = { outline: "2px solid #198754", outlineOffset: "2px" }
const styleRemoved = { outline: "2px solid #dc3545", outlineOffset: "2px" }

const groupBySubject = (permissions) =>
    Object.groupBy(permissions ?? [], (p) =>
        groups
            .flatMap((g) => g.items)
            .filter((s) => p.endsWith(s))
            .pop()
    )

export const PermissionSelector = ({ activePermissions, defaultPermissions, setPermissions }) => {
    const { permissions: currentUserPermissions } = useRole()

    const activePermsBySubject = useMemo(
        () => groupBySubject(activePermissions),
        [activePermissions]
    )
    const enabledPermsBySubject = useMemo(
        () => groupBySubject(currentUserPermissions),
        [currentUserPermissions]
    )
    const defaultPermsBySubject = useMemo(
        () => groupBySubject(defaultPermissions),
        [defaultPermissions]
    )

    const togglePermission = useCallback(
        (perm, checked) => {
            setPermissions((prev) => {
                const activePerms = new Set(prev)
                if (checked) {
                    activePerms.add(perm)
                } else {
                    activePerms.delete(perm)
                }
                return Array.from(activePerms)
            })
        },
        [setPermissions]
    )

    return groups.map((group) => (
        <div className="mb-4" key={group.title}>
            {group.title && <h3 className="mb-3">{group.title}</h3>}
            {group.items.map((subject) => (
                <div className="mb-4" key={subject}>
                    <h4 className="mb-2">
                        {subject
                            .replace(/^Conf_/, "")
                            .replace(/_/g, " ")
                            .replace(/([a-z])([A-Z])/g, "$1 $2")}
                    </h4>
                    <div className="table-responsive">
                        <table className="table table-sm align-middle">
                            <thead>
                                <tr>
                                    <th style={{ width: "220px" }}>Scope</th>
                                    <th className="text-center" style={{ width: "80px" }}>
                                        All
                                    </th>
                                    {actionOrder.map((action) => (
                                        <th key={action} className="text-center">
                                            {action}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                <Subject
                                    subject={subject}
                                    checked={activePermsBySubject[subject] ?? []}
                                    enabled={enabledPermsBySubject[subject] ?? []}
                                    defaults={
                                        defaultPermsBySubject[subject] ??
                                        (defaultPermissions?.length > 0 ? [] : undefined)
                                    }
                                    togglePermission={togglePermission}
                                />
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}
        </div>
    ))
}

const Subject = memo(
    ({ subject: s, checked, enabled, defaults, togglePermission }) =>
        levelOrder.map((l) => (
            <tr key={l}>
                <td>{l}</td>
                <td className="text-center">
                    <input
                        type="checkbox"
                        className="form-check-input"
                        title={actionOrder.map((a) => perms[s][l][a]).join(",")}
                        checked={actionOrder.every((a) => checked.includes(perms[s][l][a]))}
                        disabled={!actionOrder.every((a) => enabled.includes(perms[s][l][a]))}
                        onChange={(e) =>
                            actionOrder.forEach((a) =>
                                togglePermission(perms[s][l][a], e.target.checked)
                            )
                        }
                    />
                </td>
                {actionOrder.map((a) => (
                    <td key={a} className="text-center">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            title={perms[s][l][a]}
                            checked={checked.includes(perms[s][l][a])}
                            disabled={!enabled.includes(perms[s][l][a])}
                            onChange={(e) => togglePermission(perms[s][l][a], e.target.checked)}
                            style={
                                defaults === undefined
                                    ? undefined
                                    : checked.includes(perms[s][l][a]) &&
                                      !defaults.includes(perms[s][l][a])
                                    ? styleAdded
                                    : !checked.includes(perms[s][l][a]) &&
                                      defaults.includes(perms[s][l][a])
                                    ? styleRemoved
                                    : undefined
                            }
                        />
                    </td>
                ))}
            </tr>
        )),
    (prev, next) =>
        prev.subject === next.subject &&
        prev.enabled === next.enabled &&
        prev.defaults === next.defaults &&
        prev.togglePermission === next.togglePermission &&
        prev.checked.length === next.checked.length &&
        prev.checked.every((p, i) => next.checked[i] === p)
)
